import React, { useState } from "react";
import { X, Flag, CheckCircle } from "lucide-react";

import { Button } from "./ui/button";
import reportApi from "../api/reportApi";

const REPORT_CATEGORIES = [
  { value: "inappropriate_content", label: "Inappropriate Content" },
  { value: "spam", label: "Spam" },
  { value: "harassment", label: "Harassment" },
  { value: "fake_profile_listing", label: "Fake Profile/Listing" },
  { value: "scam_fraud", label: "Scam/Fraud" },
  { value: "violence_threats", label: "Violence/Threats" },
  { value: "other", label: "Other" }
];

export const ReportModal = ({ 
  isOpen, 
  onClose, 
  targetId, 
  targetType, // 'user', 'listing', 'chat'
  targetName 
}) => {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (!selectedCategory) {
    setError("Please select a reason for reporting");
    return;
  }

  if (selectedCategory === "other" && !customReason.trim()) {
    setError("Please provide a reason");
    return;
  }

  if (customReason.length > 500) {
    setError("Reason must be less than 500 characters");
    return;
  }

  setIsSubmitting(true);
  setError("");

  try {
    const reason =
      selectedCategory === "other"
        ? customReason.trim()
        : REPORT_CATEGORIES.find((cat) => cat.value === selectedCategory)?.label;

    const normalizedType = targetType === 'conversation' ? 'chat' : targetType.toLowerCase();

    const payload = {
      type: normalizedType,
      targetId,
      reason
    };

    console.log("Submitting report:", payload);

    await reportApi.createReport(payload);

    setShowSuccess(true);

    setTimeout(() => {
      handleClose();
    }, 3000);

  } catch (error) {
    console.error("Error submitting report:", error);
    
    // Check if it's a 400 error (already reported)
    if (error.response && error.response.status === 400) {
      setError("You have already reported this. Thank you for helping keep our community safe.");
    } else {
      setError(error.message || "Failed to submit report. Please try again.");
    }
  } finally {
    setIsSubmitting(false);
  }
};


  const handleClose = () => {
    setSelectedCategory("");
    setCustomReason("");
    setError("");
    setShowSuccess(false);
    setIsSubmitting(false);
    onClose();
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setError("");
    if (category !== "other") {
      setCustomReason("");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {showSuccess ? (
          // Success State
          <div className="p-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Report Submitted Successfully
            </h3>
            <p className="text-gray-600">
              Thank you for helping keep our community safe. We'll review your report shortly.
            </p>
          </div>
        ) : (
          // Report Form
          <>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <Flag className="w-5 h-5 text-red-500" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Report {targetType === 'chat' ? 'Conversation' : targetType === 'listing' ? 'Listing' : 'User'}
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isSubmitting}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  You are reporting {targetName ? `"${targetName}"` : `this ${targetType}`}. 
                  Please select the reason that best describes the issue.
                </p>

                {/* Category Selection */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for reporting *
                  </label>
                  {REPORT_CATEGORIES.map((category) => (
                    <label
                      key={category.value}
                      className="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="category"
                        value={category.value}
                        checked={selectedCategory === category.value}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        className="text-red-500 focus:ring-red-500"
                        disabled={isSubmitting}
                      />
                      <span className="text-sm text-gray-700">{category.label}</span>
                    </label>
                  ))}
                </div>

                {/* Custom Reason Textarea */}
                {selectedCategory === "other" && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Please explain *
                    </label>
                    <textarea
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      placeholder="Please provide details about why you're reporting this..."
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                      rows={4}
                      maxLength={500}
                      disabled={isSubmitting}
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-gray-500">
                        Be specific to help us understand the issue
                      </span>
                      <span className="text-xs text-gray-500">
                        {customReason.length}/500
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || !selectedCategory}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {isSubmitting ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    "Submit Report"
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};